import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { submitLead, otpSend, verifyLeadOtp, markLeadUnverified } from "@/lib/api";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { OtpModal } from "@/components/auth/OtpModal";
import { useContent } from "@/lib/cms";

const DEFAULT_FIELDS = {
  name:    { label: "Name",                   placeholder: "Your full name" },
  phone:   { label: "WhatsApp number",        placeholder: "+91 98xxx xxxxx" },
  company: { label: "Company",                placeholder: "Company name" },
  volume:  { label: "Monthly message volume", placeholder: "Select range" },
};

const DEFAULT_VOLUMES = ["< 1,000", "1,000 – 10,000", "10,000 – 50,000", "50,000 – 1,00,000", "1,00,000+"];

export const HeroLeadForm = () => {
  const { data: hero } = useContent("hero", {});
  const lf = hero?.lead_form || {};
  const f = { ...DEFAULT_FIELDS, ...(lf.fields || {}) };
  const volumeOptions = (lf.volume_options && lf.volume_options.length) ? lf.volume_options : DEFAULT_VOLUMES;
  const submitLabel = lf.submit_label || "Book Demo";
  const disclaimer = lf.disclaimer || "We'll send a 6-digit code to your WhatsApp to verify it's really you. We respond within 1 working hour.";
  const calendlyUrl = lf.calendly_url || "https://calendly.com/promotionkart/whatsapp-business-api-1";
  const autoOpenCalendly = lf.auto_open_calendly_on_verify !== false;
  const successTitle = lf.success_title || "Thanks, {first_name}!";
  const successVerified = lf.success_message_verified || "Your WhatsApp number is verified. We've opened your Calendly booking page in a new tab so you can pick a slot.";
  const successUnverified = lf.success_message_unverified || "Our WhatsApp API expert will call you within the next working hour and share a Calendly link to schedule your demo.";
  const errorOtpFailed = lf.error_otp_failed || "Couldn't send WhatsApp OTP. Our expert will still reach out shortly.";
  const errorSubmissionFailed = lf.error_submission_failed || "Submission failed";
  const toastVerified = lf.toast_verified || "Verified! Opening your booking calendar…";

  const [form, setForm] = useState({ name: "", phone: "", company: "", monthly_volume: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e?.target ? e.target.value : e }));

  // OTP state
  const [otpOpen, setOtpOpen] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const phoneRef = useRef("");
  const leadIdRef = useRef(null);
  const verifiedRef = useRef(false);

  // Safety net for leaving page mid-flow
  useEffect(() => {
    const onBeforeUnload = () => {
      if (leadIdRef.current && !verifiedRef.current) {
        try {
          const url = `${process.env.REACT_APP_BACKEND_URL}/api/leads/${leadIdRef.current}/mark-unverified`;
          if (navigator.sendBeacon) navigator.sendBeacon(url, new Blob([], { type: "application/json" }));
        } catch { /* ignore */ }
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  // Section can be hidden via CMS toggle
  if (lf.enabled === false) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // 1) Persist lead first
      const lead = await submitLead({
        name: form.name,
        company: form.company,
        phone: form.phone,
        monthly_volume: form.monthly_volume || null,
        source: "hero-form",
        email: null,
        message: form.monthly_volume ? `Monthly volume: ${form.monthly_volume}` : "",
      });
      setLeadId(lead.id);
      leadIdRef.current = lead.id;
      verifiedRef.current = false;
      phoneRef.current = form.phone;

      // 2) Send WhatsApp OTP
      const r = await otpSend(form.phone, "lead");
      if (!r?.success) {
        toast.warning(errorOtpFailed);
        setDone(true);
        return;
      }
      setOtpOpen(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(typeof msg === "string" ? msg : errorSubmissionFailed);
    } finally {
      setLoading(false);
    }
  };

  const onVerify = async (code) => {
    if (!leadId) throw new Error("Missing lead reference");
    const r = await verifyLeadOtp(leadId, code);
    if (!r?.verified) throw new Error(r?.message || "Incorrect OTP");
    verifiedRef.current = true;
    setDone(true);
    toast.success(toastVerified);
    if (autoOpenCalendly && calendlyUrl && typeof window !== "undefined") {
      setTimeout(() => {
        window.open(calendlyUrl, "_blank", "noopener,noreferrer");
      }, 600);
    }
  };

  const onResend = async () => {
    await otpSend(phoneRef.current, "lead");
  };

  const handleOtpClose = () => {
    setOtpOpen(false);
    if (leadIdRef.current && !verifiedRef.current) {
      markLeadUnverified(leadIdRef.current);
    }
    setDone(true);
  };

  const firstName = form.name.trim().split(/\s+/)[0] || "we got your details";
  const renderedSuccessTitle = successTitle.replace("{first_name}", firstName);

  return (
    <section className="bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 -mt-6 sm:-mt-10 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          data-testid="hero-lead-form-card"
          className="rounded-3xl border border-slate-200 bg-white shadow-xl p-5 sm:p-7"
        >
          {done ? (
            <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left" data-testid="hero-form-success">
              <div className="h-12 w-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <div>
                <div className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {renderedSuccessTitle}
                </div>
                <div className="text-sm text-slate-600">
                  {verifiedRef.current ? successVerified : successUnverified}
                </div>
              </div>
            </div>
          ) : (
          <form onSubmit={submit} data-testid="hero-lead-form" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div>
                <Label htmlFor="hf-name" className="text-xs">{f.name.label}</Label>
                <Input id="hf-name" data-testid="hero-form-name" value={form.name} onChange={set("name")} required minLength={2} placeholder={f.name.placeholder} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="hf-phone" className="text-xs">{f.phone.label}</Label>
                <Input id="hf-phone" data-testid="hero-form-phone" value={form.phone} onChange={set("phone")} required minLength={8} placeholder={f.phone.placeholder} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="hf-company" className="text-xs">{f.company.label}</Label>
                <Input id="hf-company" data-testid="hero-form-company" value={form.company} onChange={set("company")} required placeholder={f.company.placeholder} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="hf-volume" className="text-xs">{f.volume.label}</Label>
                <Select value={form.monthly_volume} onValueChange={set("monthly_volume")}>
                  <SelectTrigger id="hf-volume" data-testid="hero-form-volume" className="mt-1.5">
                    <SelectValue placeholder={f.volume.placeholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {volumeOptions.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="hero-form-submit"
                  className="w-full rounded-full h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>{submitLabel} <Send className="ml-2 h-4 w-4" /></>)}
                </Button>
              </div>
            </form>
          )}
          {!done && disclaimer && (
            <p className="mt-3 text-[11px] text-slate-500 text-center sm:text-left">{disclaimer}</p>
          )}
        </motion.div>
      </div>

      <OtpModal
        open={otpOpen}
        onClose={handleOtpClose}
        mobileMasked={(phoneRef.current || "").slice(-4)}
        title="Verify your WhatsApp number"
        description={<>We&apos;ve sent a 6-digit code to your WhatsApp <b>+91 {(phoneRef.current || "").slice(-4).padStart(10, "•")}</b></>}
        onVerify={onVerify}
        onResend={onResend}
        onChangeNumber={handleOtpClose}
        testIdPrefix="hero-lead-otp"
      />
    </section>
  );
};
